import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faTimes, faUser, faSearch, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { clinicsService, type Patient } from '../../services/clinicsService';
import BtnTaratic from '../ui/BtnTaratic';
import { createPortal } from 'react-dom';
import { useNotifications } from '../ui/notifications';

interface PatientLinkPanelProps {
  threadId: string;
  customerId: string; // The chat_customer_id
  chatChannel: string; // e.g. "instagram"
  currentPatientId?: number;
}

export const PatientLinkPanel: React.FC<PatientLinkPanelProps> = ({ threadId: _threadId, customerId, chatChannel, currentPatientId }) => {
  const notifications = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showNewPatient, setShowNewPatient] = useState(false);
  const [newPatient, setNewPatient] = useState({ full_name: '', email: '', phone: '' });

  const panelRef = useRef<HTMLDivElement>(null);

  const fetchPatients = async (query: string = '') => {
    try {
      setLoading(true);
      const res = await clinicsService.getPatients({ search: query, per_page: 10 });
      setPatients(res.patients || []);
    } catch (e) {
      console.error("Failed to fetch patients", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPatients(search);
    }
  }, [isOpen]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isOpen) fetchPatients(search);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const handleLinkPatient = async (patientId: number) => {
    try {
      setLinking(true);
      await clinicsService.bindPatientToChat(patientId, {
        chat_channel: chatChannel,
        chat_customer_id: customerId
      });
      notifications.success('Paciente vinculado', 'El paciente se vinculó correctamente al chat.');
      setIsOpen(false);
    } catch (e) {
      console.error("Failed to link patient", e);
      notifications.error('No se pudo vincular el paciente', 'Inténtalo de nuevo.');
    } finally {
      setLinking(false);
    }
  };

  const handleCreatePatient = async () => {
    const patientName = newPatient.full_name.trim();
    if (!patientName) {
      notifications.warning('Revisa los datos', 'El nombre del paciente es obligatorio.');
      return;
    }
    try {
      setLinking(true);
      const created = await clinicsService.createPatient({
        full_name: patientName,
        email: newPatient.email.trim() || null,
        phone: newPatient.phone.trim() || null,
      });
      await clinicsService.bindPatientToChat(created.id, { chat_channel: chatChannel, chat_customer_id: customerId });
      setNewPatient({ full_name: '', email: '', phone: '' });
      setShowNewPatient(false);
      await fetchPatients('');
      notifications.success('Paciente creado', `${patientName} se creó y vinculó correctamente.`);
      setIsOpen(false);
    } catch (e) {
      console.error('Failed to create patient', e);
      notifications.error('No se pudo crear el paciente', 'Revisa los datos e inténtalo de nuevo.');
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <BtnTaratic
        color="ghost-dark"
        onClick={() => setIsOpen(true)}
        className="px-3 py-1.5 flex items-center gap-2"
        title="Vincular Paciente"
      >
        <FontAwesomeIcon icon={faLink} className="icon-inner-glow-white" />
        <span className="text-sm font-medium">Vincular</span>
      </BtnTaratic>

      {isOpen && createPortal(
        <div data-ui="chat-utility-modal-overlay" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div
            ref={panelRef}
            data-ui="chat-utility-modal"
            className="chat-utility-modal bg-slate-950/95 text-white backdrop-blur-xl border border-white/15 rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden"
            style={{ maxHeight: '80vh' }}
          >
            <div data-ui="chat-utility-modal-header" className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-gradient-to-r from-blue-950/80 to-slate-950">
              <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faLink} className="text-blue-400" />
                Vincular Paciente a Chat
              </h2>
              <p className="text-xs text-slate-400 mt-1">Busca un paciente existente o crea uno nuevo sin salir del chat.</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                data-ui="chat-utility-modal-close"
                className="text-white bg-slate-800 hover:bg-slate-700 transition-colors w-9 h-9 flex items-center justify-center rounded-full border border-white/10"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div data-ui="chat-patient-search-panel" className="p-5 border-b border-white/10 bg-slate-900/65">
              <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar paciente por nombre, email, teléfono..."
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                />
              </div>
              <BtnTaratic color="blue" onClick={() => setShowNewPatient(value => !value)} className="shrink-0 flex items-center gap-2">
                <FontAwesomeIcon icon={faUserPlus} /> Nuevo paciente
              </BtnTaratic>
              </div>
              {showNewPatient && (
                <div data-ui="chat-new-patient-form" className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-2xl border border-blue-400/20 bg-blue-500/10 p-3">
                  <input value={newPatient.full_name} onChange={e => setNewPatient(current => ({ ...current, full_name: e.target.value }))} placeholder="Nombre completo *" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
                  <input value={newPatient.phone} onChange={e => setNewPatient(current => ({ ...current, phone: e.target.value }))} placeholder="Teléfono" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
                  <input value={newPatient.email} onChange={e => setNewPatient(current => ({ ...current, email: e.target.value }))} placeholder="Email" className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white" />
                  <div className="sm:col-span-3 flex justify-end gap-2">
                    <BtnTaratic color="ghost-dark" onClick={() => setShowNewPatient(false)}>Cancelar</BtnTaratic>
                    <BtnTaratic color="blue" disabled={linking || !newPatient.full_name.trim()} onClick={handleCreatePatient} className="disabled:cursor-not-allowed disabled:pointer-events-auto">Crear y vincular</BtnTaratic>
                  </div>
                </div>
              )}
            </div>

            <div data-ui="chat-utility-modal-content" className="flex-1 overflow-y-auto p-4 space-y-2">
              {loading && patients.length === 0 ? (
                <div className="text-center text-white/50 text-sm">Buscando...</div>
              ) : patients.length === 0 ? (
                <div className="text-center text-white/50 text-sm">No se encontraron pacientes.</div>
              ) : (
                [...patients].sort((a, b) => {
                  if (a.id === currentPatientId) return -1;
                  if (b.id === currentPatientId) return 1;
                  return 0;
                }).map(p => {
                  const isLinked = p.id === currentPatientId;
                  return (
                    <div data-ui="chat-patient-result" key={p.id} className={`bg-slate-900/75 border rounded-2xl p-3 flex justify-between items-center gap-3 ${isLinked ? 'border-green-500/50 bg-green-500/10' : 'border-slate-700'}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-900/30 text-blue-400 flex items-center justify-center shrink-0">
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white flex gap-2 items-center">
                            <span className="text-blue-400/70 text-xs bg-blue-400/10 px-1.5 py-0.5 rounded">#{p.id}</span>
                            {p.full_name}
                            {isLinked && (
                              <span className="text-[10px] bg-green-500/20 text-green-400 border border-green-500/30 px-1.5 py-0.5 rounded uppercase tracking-wider font-bold">
                                Vinculado
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-white/50 mt-1 flex items-center gap-3">
                            {p.phone && <span>📞 {p.phone}</span>}
                            {p.email && <span>✉️ {p.email}</span>}
                            {!p.phone && !p.email && <span>Sin contacto</span>}
                          </div>
                        </div>
                      </div>
                      <BtnTaratic
                        color={isLinked ? "ghost-dark" : "blue"}
                        onClick={() => handleLinkPatient(p.id)}
                        disabled={linking || isLinked}
                        className="w-28 shrink-0 flex items-center justify-center"
                      >
                        {isLinked ? 'Vinculado' : 'Vincular'}
                      </BtnTaratic>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
